// @ts-strict-ignore
import * as Linkify from 'linkifyjs';
import 'linkify-plugin-mention';
import { keyBy } from 'lodash';

export const MBChatInputUtil = {
  mentionedUsers(content: string, users: MBAPI.ShortUser[]): MBAPI.ShortUser[] {
    // 1. Get all mentions from the content. Linkify is the same
    // module as we use on the backend.
    const matches = Linkify.find(content);
    const mentions = matches.filter(match => match.type === 'mention').map(match => match.value);

    // 2. Put all mentions in a dict to make it easy
    //    to see if a username has been mentioned
    const mentionsByUsername: Dict<string> = keyBy(mentions);

    // 3. If we have mentioned "@all", return all users
    //    TODO(fant): how do we do this in a smart way for groups?
    //    Suggestion: get all groups and a list of associated
    //    usernames. Then if there is a mentioned group, we
    //    simply return that group's users.
    if (!!mentionsByUsername['@all']) {
      return users;
    }

    // 4. Return all specifically mentioned users.
    //    This might be an empty array
    return users.filter(user => !!mentionsByUsername[`@${user.username}`]);
  },

  // This function calculates how many users will be
  // notified when an urgent message is sent. If
  // specific people are mentioned, only they will
  // be notified. Otherwise everyone will be notified.
  urgentRecipients(content: string, users: MBAPI.ShortUser[]): MBAPI.ShortUser[] {
    // 1. Filter away other users. We will never notify the current user
    const otherUsers = users.filter(user => !user.isMe);
    // 2. Get all mentioend users
    const mentionedUsers = MBChatInputUtil.mentionedUsers(content, otherUsers);
    // 3. If we have mentioned users, return them
    if (!!mentionedUsers.length) {
      return mentionedUsers;
    }
    // 4. Otherwise return all users (except me)
    return otherUsers;
  },

  numUrgentRecipients(content: string, users: MBAPI.ShortUser[]): number {
    return MBChatInputUtil.urgentRecipients(content, users).length;
  },

  /*
        This function returns the current @mention, if any.
        In the examples belof "|" is where the cursor is.
        "@fa|nt " => @fant
        "@fant|" => @fant
        "@fant |" => -
        "|@fant" => @fant
        "| @fant" => @fant
        "new line
        |@fant" => @fant
    */
  mentionQuery(string: string, cursorPosition: number): string {
    // 1. Find the string between the cursor and the next space/line break/tab
    // "\s" regex matches spaces, line breaks and tabs
    // https://stackoverflow.com/a/4377521/12338002
    const currentWordStart = string.substr(0, cursorPosition).split(/\s/).reverse()[0]; // get last item
    // 2. Find the string between the cursor and the previous space/line break/tab
    const currentWordEnd = string.substr(cursorPosition).split(/\s/)[0];
    // 3. The current word is the combination of these two
    const currentWord = `${currentWordStart}${currentWordEnd}`;

    // 4. If the word starts with "@", we say that it's a mention
    if (currentWord[0] !== '@') {
      return undefined;
    }
    // 5. The returned mention is the word after "@"
    return currentWord.substr(1);
  },

  addMention(
    string: string,
    cursorPosition: number,
    mention: string,
  ): {
    string: string;
    cursorPosition: number;
  } {
    // 1. To insert a mention, we first get all the text
    //    before and after the current mention query.
    //    Eg "hello @fa|nt end" (where "|" is the cursor position)
    //    => before: "hello @fa"
    //    => after:  "nt end"
    const before = string.substr(0, cursorPosition);
    const after = string.substr(cursorPosition);

    // 2. When adding a mention, we assume that the cursor is currently
    //    adjacent to a mention query (eg "@fa"). We want to remove this
    //    mention query so that we only have the raw content before/after.
    //    Eg before: "hello @fa" => "hello "
    //    Eg after:  "nt end" => " end"
    //    The way the regex works is by replacing all non-space symbols (\S*)
    //    either before or after the cursor. To only replace one group of a
    //    regex match, see this answer:
    //    https://stackoverflow.com/a/3954957
    //
    // /(\s|^)(\S*)$/ => (space/tab/newline/startofstring)(non-space)(end)
    // '$1' means we keep the first group in the regex
    // (aka the space/tab/newline/startofstring)
    const beforeWithoutMentionQuery = before.replace(/(\s|^)(\S*)$/, '$1');
    // /^(\S*)(\s|$)/ => (start)(non-space)(space/tab/newline/endofstring)
    // '$2' means we keep the second group in the regex
    // (aka the space/tab/newline/endofstring)
    // We also do another replace to make sure that we get rid of leading spaces
    const afterWithoutMentionQuery = after.replace(/^(\S*)(\s|$)/, '$2').replace(/^[ ]/, '');

    // 3. Between the content before and after we want to have a separator.
    //
    // If there is no content before OR we have space/newline/tab before, we
    // don't need to have a separator before the mention. Otherwise we add a space.
    const beforeSeparator =
      !beforeWithoutMentionQuery || !!beforeWithoutMentionQuery.match(/\s$/) ? '' : ' ';
    // If the string after does not start with a space, we add a space.
    const afterSeparator = !!afterWithoutMentionQuery.match(/^[ ]/) ? '' : ' ';

    // 4. Construct the new string (except for the after part). We use
    //    this string's length to figure out where to put the cursor.
    const stringWithoutAfter = `${beforeWithoutMentionQuery}${beforeSeparator}@${mention}${afterSeparator}`;

    return {
      string: `${stringWithoutAfter}${afterWithoutMentionQuery}`,
      cursorPosition: stringWithoutAfter.length,
    };
  },
};
